import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUpdateProfile, useProfileByUsernameOrId } from '@/hooks/useProfile';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import { UserProfileStatus, Gender, UserProfileUpdateRequest } from '@/types/userProfile';

/** Normalize API date (ISO or YYYY-MM-DD) to YYYY-MM-DD for date input. */
function toDateOnly(value: string | undefined): string {
  if (!value?.trim()) return '';
  const part = value.trim().split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : value.trim();
}

const profileSchema = z.object({
  userName: z.string().max(50).optional(),
  email: z.string().email('Invalid email address'),
  firstName: z.string().max(50).optional(),
  lastName: z.string().max(50).optional(),
  dateOfBirth: z
    .string()
    .optional()
    .refine((s) => !s || s.trim() === '' || /^\d{4}-\d{2}-\d{2}$/.test(s.trim()), 'Date must be YYYY-MM-DD'),
  gender: z.nativeEnum(Gender),
  status: z.nativeEnum(UserProfileStatus),
  isOnboardingCompleted: z.boolean().optional(),
  emailVerified: z.boolean().optional(),
  phoneNumber: z.object({
    countryCode: z.string().min(1, 'Country code is required'),
    number: z.string().min(1, 'Phone number is required'),
    verified: z.boolean().optional(),
  }).optional().nullable(),
  location: z.object({
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    country: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    postalCode: z.string().optional().nullable(),
    timezone: z.string().optional().nullable(),
    isPrecise: z.boolean().optional(),
  }).optional().nullable(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const ProfileFormPage: React.FC = () => {
  const { userName } = useParams<{ userName: string }>();
  const navigate = useNavigate();
  const { data: profile, isLoading, error: profileError } = useProfileByUsernameOrId(userName);
  const updateProfile = useUpdateProfile();

  const userId = profile?.userId;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: profile ? {
      userName: profile.userName || '',
      email: profile.email,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      dateOfBirth: toDateOnly(profile.dateOfBirth) || '',
      gender: profile.gender,
      status: profile.status,
      isOnboardingCompleted: profile.isOnboardingCompleted || false,
      emailVerified: profile.emailVerified || false,
      phoneNumber: profile.phoneNumber,
      location: profile.location,
    } : undefined,
    values: profile ? {
      userName: profile.userName || '',
      email: profile.email,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      dateOfBirth: toDateOnly(profile.dateOfBirth) || '',
      gender: profile.gender,
      status: profile.status,
      isOnboardingCompleted: profile.isOnboardingCompleted || false,
      emailVerified: profile.emailVerified || false,
      phoneNumber: profile.phoneNumber,
      location: profile.location,
    } : undefined,
  });

  const onSubmit = async (data: ProfileFormData) => {
    if (!userId) return;

    try {
      // Build a robust update request satisfying API requirements
      const updateRequest: UserProfileUpdateRequest = {
        userName: data.userName?.trim() || undefined,
        email: data.email,
        emailVerified: data.emailVerified ?? false,
        firstName: data.firstName?.trim() || undefined,
        lastName: data.lastName?.trim() || undefined,
        dateOfBirth: data.dateOfBirth?.trim() ? toDateOnly(data.dateOfBirth) || undefined : undefined,
        gender: data.gender,
        phoneNumber: data.phoneNumber || undefined,
        location: data.location ? {
          latitude: data.location.latitude,
          longitude: data.location.longitude,
          country: data.location.country || undefined,
          state: data.location.state || undefined,
          city: data.location.city || undefined,
          postalCode: data.location.postalCode || undefined,
          timezone: data.location.timezone || undefined,
          isPrecise: data.location.isPrecise,
        } : undefined,
        status: data.status,
        isOnboardingCompleted: data.isOnboardingCompleted ?? false,
        // Ensure required objects are never empty/invalid
        preferences: {
          preferredLanguage: profile?.preferences?.preferredLanguage || 'en',
          preferredTheme: profile?.preferences?.preferredTheme || 'light',
          timezone: profile?.preferences?.timezone || profile?.location?.timezone || undefined,
          additionalPreferences: profile?.preferences?.additionalPreferences || {},
        },
        gdprCompliance: {
          dataProcessingConsent: profile?.gdprCompliance?.dataProcessingConsent ?? false,
          consentGivenAt: profile?.gdprCompliance?.consentGivenAt,
          consentWithdrawnAt: profile?.gdprCompliance?.consentWithdrawnAt,
          dataExportRequested: profile?.gdprCompliance?.dataExportRequested ?? false,
          dataDeletionRequested: profile?.gdprCompliance?.dataDeletionRequested ?? false,
        },
        termsAcceptance: {
          termsVersion: profile?.termsAcceptance?.termsVersion || '1.0.0',
          acceptedAt: profile?.termsAcceptance?.acceptedAt,
          acceptedIpAddress: profile?.termsAcceptance?.acceptedIpAddress,
          acceptedUserAgent: profile?.termsAcceptance?.acceptedUserAgent,
        },
        userConsent: {
          termsAccepted: profile?.userConsent?.termsAccepted ?? false,
          privacyPolicyAccepted: profile?.userConsent?.privacyPolicyAccepted ?? false,
          marketingEmails: profile?.userConsent?.marketingEmails ?? false,
          smsNotifications: profile?.userConsent?.smsNotifications ?? false,
          pushNotifications: profile?.userConsent?.pushNotifications ?? false,
          dataAnalytics: profile?.userConsent?.dataAnalytics ?? false,
          thirdPartySharing: profile?.userConsent?.thirdPartySharing ?? false,
          cookieConsent: profile?.userConsent?.cookieConsent ?? false,
          consentTimestamp: profile?.userConsent?.consentTimestamp,
          consentVersion: profile?.userConsent?.consentVersion,
        },
      };
      
      const updated = await updateProfile.mutateAsync({ userId, data: updateRequest });
      const nextIdentifier = updated?.userName || updated?.userId || userId;
      navigate(`/admin/profiles/${encodeURIComponent(nextIdentifier)}`);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading profile..." />;
  }

  if (profileError || !profile) {
    return <ErrorState error={profileError || new Error('Profile not found')} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/admin/profiles/${userName}`}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Profile</h1>
            <p className="text-muted-foreground mt-2">Update user profile information</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="userId">User ID</Label>
              <Input id="userId" value={profile.userId} disabled className="mt-1" />
            </div>

            <div>
              <Label htmlFor="userName">Username</Label>
              <Input
                id="userName"
                {...register('userName')}
                maxLength={50}
                className="mt-1"
              />
              {errors.userName && (
                <p className="text-xs text-destructive mt-1">{errors.userName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                className="mt-1"
              />
              {errors.email && (
                <p className="text-xs text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                {...register('firstName')}
                maxLength={50}
                className="mt-1"
              />
              {errors.firstName && (
                <p className="text-xs text-destructive mt-1">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                {...register('lastName')}
                maxLength={50}
                className="mt-1"
              />
              {errors.lastName && (
                <p className="text-xs text-destructive mt-1">{errors.lastName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={watch('dateOfBirth') ?? ''}
                onChange={(e) => setValue('dateOfBirth', e.target.value, { shouldValidate: true })}
                className="mt-1"
              />
              {errors.dateOfBirth && (
                <p className="text-xs text-destructive mt-1">{errors.dateOfBirth.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="gender">Gender *</Label>
              <Select
                value={watch('gender')}
                onValueChange={(value) => setValue('gender', value as Gender)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(Gender).map((gender) => (
                    <SelectItem key={gender} value={gender}>
                      {gender}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-xs text-destructive mt-1">{errors.gender.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as UserProfileStatus)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(UserProfileStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.status && (
                <p className="text-xs text-destructive mt-1">{errors.status.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isOnboardingCompleted"
                checked={watch('isOnboardingCompleted') || false}
                onChange={(e) => setValue('isOnboardingCompleted', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="isOnboardingCompleted" className="cursor-pointer">
                Onboarding Completed
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="emailVerified"
                checked={watch('emailVerified') || false}
                onChange={(e) => setValue('emailVerified', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="emailVerified" className="cursor-pointer">
                Email Verified
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle>Location</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                {...register('location.country')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                {...register('location.state')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                {...register('location.city')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="postalCode">Postal Code</Label>
              <Input
                id="postalCode"
                {...register('location.postalCode')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                {...register('location.timezone')}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" asChild>
            <Link to={`/admin/profiles/${userName}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={!isDirty || updateProfile.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileFormPage;







